// src/services/assets/assetLedgerService.js

/**
 * =====================================================
 * ASSET LEDGER SERVICE
 * =====================================================
 *
 * PURPOSE:
 * - Builds replay-safe asset ledger
 * - Derives current holder from transaction history
 * - Feeds AssetLedger.js UI
 *
 * RULES:
 * - All status checks on workflow_state — not .status
 * - Only FINALIZED transactions change custody
 * - No raw status strings
 * - company_id enforced everywhere
 * - Asset fetched by doc ID — not query
 * =====================================================
 */

import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    where,
    orderBy,
} from "firebase/firestore";

import { db } from "../../firebase";

import {
    WORKFLOW_STATES,
    ACTIVE_PENDING_WORKFLOW_STATES,
} from "./workflowStates";


import {
    sortTransactionsChronologically,
    filterApprovedTransactions,
    filterPendingTransactions,
    deriveHolderFromReplay,
    replayHolderHistory,
    normalizeTimestamp,
} from "../../utils/assets/assetReplayUtils";

// =====================================================
// COLLECTIONS
// =====================================================

const ASSET_TRANSACTION_COLLECTION =
    "asset_transactions";

const ASSET_MASTER_COLLECTION =
    "asset_master";

// =====================================================
// INTERNAL — VALIDATE COMPANY
// =====================================================

const validateCompany = (companyId) => {
    if (!companyId) {
        throw new Error("company_id is required");
    }
};

// =====================================================
// FETCH ASSET MASTER
// Fetched by doc ID — not query field
// =====================================================

export const getAssetMasterData = async (
    assetId,
    companyId
) => {

    validateCompany(companyId);

    if (!assetId) {
        throw new Error("assetId is required");
    }

    // ─────────────────────────────────────────────
    // asset_master document ID = assetId
    // Use doc() not query — faster + correct
    // ─────────────────────────────────────────────

    const assetRef = doc(
        db,
        ASSET_MASTER_COLLECTION,
        assetId
    );

    const assetSnap = await getDoc(assetRef);

    if (!assetSnap.exists()) {
        throw new Error("Asset not found");
    }

    const assetData = assetSnap.data();

    if (assetData.company_id !== companyId) {
        throw new Error("Unauthorized company access");
    }

    return { id: assetSnap.id, ...assetData };
};

// =====================================================
// FETCH ASSET TRANSACTIONS
// Full ledger — all states — for replay engine
// =====================================================

export const getAssetTransactions = async (
    assetId,
    companyId
) => {

    validateCompany(companyId);

    const q = query(
        collection(db, ASSET_TRANSACTION_COLLECTION),
        where("asset_id",   "==", assetId),
        where("company_id", "==", companyId),
        orderBy("created_at", "asc")
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
    }));
};

// =====================================================
// GENERATE ASSET LEDGER
// Full replay-safe ledger for AssetLedger.js UI
// =====================================================

export const generateAssetLedger = async (
    assetId,
    companyId
) => {

    validateCompany(companyId);

    const asset =
        await getAssetMasterData(assetId, companyId);

    const transactions =
        await getAssetTransactions(assetId, companyId);

    // ─────────────────────────────────────────────
    // Sort chronologically for replay
    // ─────────────────────────────────────────────

    const sortedTransactions =
        sortTransactionsChronologically(transactions);

    // ─────────────────────────────────────────────
    // Initial holder from asset_master snapshot
    // ─────────────────────────────────────────────

    // Current holder replay se derive karo — asset_master cache pe depend mat karo
    const initialHolder = {
        holder_type: asset.current_holder_type || "STORE",
        holder_id:   asset.current_holder_id   || null,
        holder_name: asset.current_holder_name || 
                     (asset.current_holder_id === "MAIN_STORE" ? "MAIN STORE" :
                      asset.current_holder_id === "TOOL_ROOM"  ? "TOOL ROOM"  :
                      asset.current_holder_name || "MAIN STORE"),
    };

    // ─────────────────────────────────────────────
    // REPLAY ENGINE
    // ─────────────────────────────────────────────

    const replayHistory =
        replayHolderHistory(sortedTransactions, initialHolder);

    const currentHolder =
        deriveHolderFromReplay(sortedTransactions, initialHolder);

    // holder_name resolve karo — UID jaisi value ho to bhi fix karo
    const isUidLike = (name) => name && name.length > 20 && !name.includes(" ");
    if (currentHolder && (!currentHolder.holder_name || isUidLike(currentHolder.holder_name))) {
        if (currentHolder.holder_id === "MAIN_STORE") {
            currentHolder.holder_name = "MAIN STORE";
        } else if (currentHolder.holder_id === "TOOL_ROOM") {
            currentHolder.holder_name = "TOOL ROOM";
        } else if (currentHolder.holder_id) {
            try {
                const userSnap = await getDoc(doc(db, "users", currentHolder.holder_id));
                if (userSnap.exists()) {
                    currentHolder.holder_name = userSnap.data().name || currentHolder.holder_id;
                } else {
                    const siteSnap = await getDoc(doc(db, "sites", currentHolder.holder_id));
                    if (siteSnap.exists()) {
                        currentHolder.holder_name = siteSnap.data().site_name || currentHolder.holder_id;
                    } else {
                        currentHolder.holder_name = asset.current_holder_name || currentHolder.holder_id;
                    }
                }
            } catch (e) {
                currentHolder.holder_name = asset.current_holder_name || currentHolder.holder_id;
            }
        }
    }

    // ─────────────────────────────────────────────
    // BUILD LEDGER ROWS
    // ─────────────────────────────────────────────

    const ledgerRows = sortedTransactions.map(
        (transaction, index) => {

            const replayItem = replayHistory[index];

            return {
                sequence:         index + 1,
                transaction_id:   transaction.id,
                asset_id:         asset.id,
                asset_name:       asset.asset_name       || "",
                asset_category:   asset.asset_category   || "",
                ownership_mode:   asset.ownership_mode   || "",

                movement_type:    transaction.movement_type    || "TRANSFER",

                // ─────────────────────────────────────
                // workflow fields — not legacy .status
                // ─────────────────────────────────────
                workflow_state:    transaction.workflow_state,
                workflow_type:     transaction.workflow_type,
                transaction_class: transaction.transaction_class,
                status:            transaction.workflow_state, // UI compat alias

                from_type:        transaction.from_type || null,
                from_id:          transaction.from_id   || null,
                from_holder_name:
                    transaction.from_snapshot?.holder_name ||
                    transaction.from_holder_name ||
                    transaction.from_id || "-",

                to_type:          transaction.to_type || null,
                to_id:            transaction.to_id   || null,
                to_holder_name:
                    transaction.to_snapshot?.holder_name ||
                    transaction.to_holder_name ||
                    transaction.to_id || "-",

                previous_holder:  replayItem?.previous_holder || null,
                resulting_holder: replayItem?.resulting_holder || null,

                requested_by:     transaction.requested_by || null,
                approved_by:      transaction.approved_by  || transaction.received_by || null,
                remarks:          transaction.remarks       || "",

                created_at:       transaction.created_at || null,
                created_timestamp: normalizeTimestamp(transaction.created_at),
            };
        }
    );

    // ─────────────────────────────────────────────
    // COUNTS
    // All on workflow_state — not .status
    // ─────────────────────────────────────────────

    const finalizedCount = filterApprovedTransactions(sortedTransactions).length;
    const pendingCount   = filterPendingTransactions(sortedTransactions).length;

    return {
        asset: {
            id:             asset.id,
            asset_name:     asset.asset_name     || "",
            asset_category: asset.asset_category || "",
            asset_type:     asset.asset_type     || "",
            serial_no:      asset.serial_no      || "",
            ownership_mode: asset.ownership_mode || "",
        },

        current_holder:         currentHolder,
        ledger_rows:            ledgerRows,
        total_transactions:     sortedTransactions.length,
        completed_transactions: finalizedCount,
        pending_transactions:   pendingCount,
    };
};

// =====================================================
// GET ASSET TIMELINE
// Simplified view for quick history display
// =====================================================

export const getAssetTimeline = async (
    assetId,
    companyId
) => {

    validateCompany(companyId);

    const ledger =
        await generateAssetLedger(assetId, companyId);

    return ledger.ledger_rows.map((row) => ({
        transaction_id: row.transaction_id,
        date:           row.created_at,
        movement:       `${row.from_type || "-"} → ${row.to_type || "-"}`,
        from:           row.from_holder_name,
        to:             row.to_holder_name,
        workflow_state: row.workflow_state,
        remarks:        row.remarks,
        requested_by:   row.requested_by,
        approved_by:    row.approved_by,
    }));
};

// =====================================================
// GET CURRENT HOLDER SUMMARY
// =====================================================

export const getCurrentHolderSummary = async (
    assetId,
    companyId
) => {

    validateCompany(companyId);

    const ledger =
        await generateAssetLedger(assetId, companyId);

    return {
        asset_id:      ledger.asset.id,
        asset_name:    ledger.asset.asset_name,
        ownership_mode: ledger.asset.ownership_mode,
        holder_type:   ledger.current_holder.holder_type,
        holder_id:     ledger.current_holder.holder_id,
        holder_name:   ledger.current_holder.holder_name,
    };
};

// =====================================================
// GET PENDING TRANSFER LEDGER
// Active pending transactions only
// Uses workflow_state — not .status
// =====================================================

export const getPendingTransferLedger = async (companyId) => {

    validateCompany(companyId);

    // ─────────────────────────────────────────────
    // Query on workflow_state — ACTIVE_PENDING only
    // ─────────────────────────────────────────────

    const q = query(
        collection(db, ASSET_TRANSACTION_COLLECTION),
        where("company_id",     "==", companyId),
        where("workflow_state", "in", ACTIVE_PENDING_WORKFLOW_STATES),
        orderBy("created_at", "desc")
    );

    const snapshot = await getDocs(q);

    const transactions = snapshot.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
    }));

    return transactions.map((transaction, index) => ({
        sequence:       index + 1,
        transaction_id: transaction.id,
        asset_id:       transaction.asset_id,
        asset_name:     transaction.asset_name,
        from_type:      transaction.from_type,
        from_id:        transaction.from_id,
        to_type:        transaction.to_type,
        to_id:          transaction.to_id,
        workflow_state: transaction.workflow_state,
        workflow_type:  transaction.workflow_type,
        requested_by:   transaction.requested_by,
        created_at:     transaction.created_at,
        remarks:        transaction.remarks || "",
    }));
};

// =====================================================
// GET HOLDER HISTORY REPORT
// Only FINALIZED transactions — custody chain only
// =====================================================

export const getHolderHistoryReport = async (
    assetId,
    companyId
) => {

    validateCompany(companyId);

    const ledger =
        await generateAssetLedger(assetId, companyId);

    return ledger.ledger_rows
        .filter(
            (row) => row.workflow_state === WORKFLOW_STATES.FINALIZED
        )
        .map((row, index) => ({
            sequence:       index + 1,
            holder_type:    row.resulting_holder?.holder_type,
            holder_id:      row.resulting_holder?.holder_id,
            holder_name:    row.resulting_holder?.holder_name,
            transaction_id: row.transaction_id,
            workflow_type:  row.workflow_type,
            approved_by:    row.approved_by,
            created_at:     row.created_at,
        }));
};

// =====================================================
// VERIFY LEDGER REPLAY SAFETY
// Compares asset_master snapshot vs replay result
// =====================================================

export const verifyLedgerReplaySafety = async (
    assetId,
    companyId
) => {

    validateCompany(companyId);

    const ledger =
        await generateAssetLedger(assetId, companyId);

    // ─────────────────────────────────────────────
    // Last FINALIZED row = last custody change
    // ─────────────────────────────────────────────

    const lastFinalizedRow =
        [...ledger.ledger_rows]
            .reverse()
            .find(
                (row) => row.workflow_state === WORKFLOW_STATES.FINALIZED
            );

    if (!lastFinalizedRow) {
        return {
            replay_safe:     true,
            reason:          "No finalized transaction found",
            current_holder:  ledger.current_holder,
        };
    }

    const replaySafe =
        String(lastFinalizedRow.resulting_holder?.holder_type || "") ===
        String(ledger.current_holder?.holder_type || "")
        &&
        String(lastFinalizedRow.resulting_holder?.holder_id || "") ===
        String(ledger.current_holder?.holder_id || "");

    return {
        replay_safe:    replaySafe,
        ledger_holder:  lastFinalizedRow.resulting_holder,
        current_holder: ledger.current_holder,
    };
};